import { useDataLayerAction } from '@components/OOKA/pylot-tag-manager/src/TagContext'
import { useCallback } from 'react'

export interface MenuTrackingData {
    menu_location: 'top_nav' | 'in_page_nav'
    menu_link_text: string
    menu_link_url: string
    menu_hierarchy_full: string
    menu_level_1: string
    menu_level_2: string
    menu_level_3: string
}

const normalizeMenuText = (text: string): string => {
    return text.trim().replaceAll(/\s*\(\d+\)\s*$/g, '')
}

const constructFullUrl = (path: string): string => {
    if (typeof window === 'undefined') {
        return path
    }

    // Construct full URL
    const protocol = window.location.protocol
    const host = window.location.host
    return `${protocol}//${host}${path.startsWith('/') ? path : '/' + path}`
}

/**
 * Hook for tracking menu clicks across the application
 *
 * @param menuLocation - The location of the menu (top_nav, in_page_nav)
 * @param menuHierarchy - Array representing the menu hierarchy (e.g., ['shop-ooka', 'device'])
 * @returns Click handler function that accepts link text and URL
 */

export const useMenuTracking = (
    menuLocation: MenuTrackingData['menu_location'],
    menuHierarchy: string[] = []
) => {
    const dataLayerAction = useDataLayerAction()

    const trackMenuClick = useCallback(
        (
            linkText: string,
            linkUrl: string,
            additionalHierarchy: string[] = []
        ) => {
            const fullHierarchy = [...menuHierarchy, ...additionalHierarchy]
            const normalizedText = normalizeMenuText(linkText)
            const fullUrl = constructFullUrl(linkUrl)

            const trackingData: MenuTrackingData = {
                menu_location: menuLocation,
                menu_link_text: normalizedText,
                menu_link_url: fullUrl,
                menu_hierarchy_full: fullHierarchy.join(' > '),
                menu_level_1: fullHierarchy[0] || '',
                menu_level_2: fullHierarchy[1] || '',
                menu_level_3: ''
            }

            try {
                dataLayerAction({
                    type: 'MENU_CLICK',
                    data: trackingData
                })
            } catch (error) {
                console.error('Error calling dataLayerAction:', error)
            }
        },
        [dataLayerAction, menuLocation, menuHierarchy]
    )

    return trackMenuClick
}

export const useTopNavTracking = (menuHierarchy: string[] = []) => {
    return useMenuTracking('top_nav', menuHierarchy)
}

export const useInPageNavTracking = (menuHierarchy: string[] = []) => {
    return useMenuTracking('in_page_nav', menuHierarchy)
}
